import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Briefcase } from "lucide-react";
import { brl } from "../lib/format";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { FormField } from "../components/ui/form-field";
import { useI18n } from "../i18n/I18nContext";

const empty = {
  name: "",
  description: "",
  default_value_per_day: 0,
  status: "ativo",
  can_see_all_jobs: false,
  can_finish_anywhere: false,
  can_start_anywhere: false,
  can_reopen_jobs: false,
};

const PERMISSIONS = [
  { key: "can_see_all_jobs", label: "Ver todas as casas", hint: "Enxerga toda a agenda, não só a da própria equipe" },
  { key: "can_start_anywhere", label: "Dar andamento sem estar no local", hint: "Inicia a casa ignorando o GPS/geofence" },
  { key: "can_finish_anywhere", label: "Finalizar sem estar no local", hint: "Finaliza ignorando GPS e sem limite de 1h" },
  { key: "can_reopen_jobs", label: "Reabrir / voltar casa", hint: "Pode reabrir casas finalizadas" },
];

export default function Positions() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    const { data } = await api.get("/positions");
    setItems(data);
  }
  useEffect(() => {
    load();
  }, []);

  async function remove(p) {
    if (!(await confirm({
      title: `${t("Excluir cargo")} "${p.name}"?`,
      destructive: true,
      confirmText: t("Excluir"),
    }))) return;
    try {
      await api.delete(`/positions/${p.id}`);
      toast.success(t("Cargo excluído"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Estrutura")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Briefcase size={26} /> {t("Cargos & Funções")}
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            {t("Cadastre os cargos usados ao registrar colaboradores")}
          </div>
        </div>
        <Button
          data-testid="position-new-btn"
          onClick={() => {
            setEditing({ ...empty });
            setOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={16} className="mr-1" /> {t("Novo cargo")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((p) => (
          <div
            key={p.id}
            data-testid={`position-card-${p.id}`}
            className="cm-card p-5"
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {p.description || "—"}
                </div>
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={() => {
                    setEditing(p);
                    setOpen(true);
                  }}
                  data-testid={`position-edit-${p.id}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  title={t("Editar")}
                >
                  <Pencil size={18} />
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`position-delete-${p.id}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                  title={t("Excluir")}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            <div className="flex items-end justify-between mt-3">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                  {t("Valor padrão / dia")}
                </div>
                <div className="font-bold text-slate-900">
                  {brl(p.default_value_per_day || 0)}
                </div>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  p.status === "ativo"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-slate-100 text-slate-600 border border-slate-200"
                }`}
              >
                {t(p.status === "ativo" ? "Ativo" : "Inativo")}
              </span>
            </div>
            {PERMISSIONS.some((perm) => p[perm.key]) && (
              <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
                {PERMISSIONS.filter((perm) => p[perm.key]).map((perm) => (
                  <span
                    key={perm.key}
                    className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    {t(perm.label)}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="cm-card p-10 text-center text-slate-500 col-span-full">
            {t("Nenhum cargo cadastrado.")}
          </div>
        )}
      </div>

      <PositionDialog
        open={open}
        onOpenChange={setOpen}
        item={editing}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />
    </AdminLayout>
  );
}

function PositionDialog({ open, onOpenChange, item, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(item || empty);
  useEffect(() => {
    setForm(item || empty);
  }, [item, open]);
  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  async function save() {
    try {
      const body = {
        ...form,
        default_value_per_day: Number(form.default_value_per_day) || 0,
      };
      if (item?.id) await api.patch(`/positions/${item.id}`, body);
      else await api.post("/positions", body);
      toast.success(t("Cargo salvo"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="position-dialog">
        <DialogHeader>
          <DialogTitle>{item?.id ? t("Editar cargo") : t("Novo cargo")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label={t("Nome do Cargo")}>
            <input
              data-testid="position-field-name"
              className="cm-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={t("Ex: Faxineira, Líder de equipe…")}
            />
          </FormField>
          <FormField label={t("Descrição")}>
            <textarea
              rows={2}
              className="cm-input"
              value={form.description || ""}
              onChange={(e) => set("description", e.target.value)}
              placeholder={t("O que esse cargo faz na operação")}
            />
          </FormField>
          <FormField label={t("Valor Padrão por Dia (US$)")}>
            <input
              data-testid="position-field-value"
              type="number"
              step="0.01"
              className="cm-input"
              value={form.default_value_per_day}
              onChange={(e) =>
                set("default_value_per_day", e.target.value)
              }
            />
          </FormField>
          <FormField label={t("Status")}>
            <select
              className="cm-input"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
            >
              <option value="ativo">{t("Ativo")}</option>
              <option value="inativo">{t("Inativo")}</option>
            </select>
          </FormField>
          <div className="pt-1">
            <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">
              {t("Permissões do cargo")}
            </div>
            <div className="space-y-2">
              {PERMISSIONS.map((p) => (
                <label
                  key={p.key}
                  data-testid={`position-perm-${p.key}`}
                  className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-blue-600"
                    checked={!!form[p.key]}
                    onChange={(e) => set(p.key, e.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-800">{t(p.label)}</span>
                    <span className="block text-xs text-slate-500">{t(p.hint)}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancelar")}
          </Button>
          <Button
            data-testid="position-save-btn"
            onClick={save}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t("Salvar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
